import { type Table } from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  entityLabelPlural?: string;
  pageSizeOptions?: readonly number[];
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  isServerPaginated?: boolean;
  serverRowCount?: number;
  serverCanNextPage?: boolean;
}

export function DataTablePagination<TData>({
  table,
  entityLabelPlural = "rows",
  pageSizeOptions = [10, 25, 50, 100],
  isLoading = false,
  isFetching = false,
  isError = false,
  isServerPaginated = false,
  serverRowCount,
  serverCanNextPage,
}: DataTablePaginationProps<TData>) {
  const totalCount = isServerPaginated
    ? serverRowCount
    : table.getPrePaginationRowModel().rows.length;
  const visibleCount = table.getRowModel().rows.length;
  const { pageIndex, pageSize } = table.getState().pagination;
  const from = visibleCount === 0 ? 0 : pageIndex * pageSize + 1;
  const pageEnd = visibleCount === 0 ? 0 : pageIndex * pageSize + visibleCount;
  const to =
    typeof totalCount === "number" ? Math.min(pageEnd, totalCount) : pageEnd;
  const pageCount = Math.max(1, table.getPageCount());
  const hasKnownPageCount =
    table.getPageCount() >= 0 &&
    (!isServerPaginated || typeof serverRowCount === "number");
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = isServerPaginated
    ? (serverCanNextPage ?? table.getCanNextPage())
    : table.getCanNextPage();
  const showControls =
    canPreviousPage || canNextPage || (hasKnownPageCount && pageCount > 1);
  const isEmpty =
    !isLoading &&
    !isFetching &&
    !isError &&
    visibleCount === 0 &&
    pageIndex === 0 &&
    !canNextPage &&
    (totalCount === 0 ||
      (isServerPaginated &&
        serverRowCount === undefined &&
        serverCanNextPage === false));
  // Group headers also occupy pagination slots, so the summary counts rows.
  const countedEntity = table.getState().grouping.length
    ? "rows"
    : entityLabelPlural;

  return (
    <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-3">
      <Text variant="subHeading">
        {isEmpty ? (
          `No ${countedEntity}`
        ) : (
          <>
            Showing {from} to {to}
            {typeof totalCount === "number" ? ` of ${totalCount}` : ""}{" "}
            {countedEntity}
          </>
        )}
      </Text>
      {showControls ? (
        <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-3">
          <div className="flex items-center gap-2">
            <Text as="span" variant="subHeading" className="whitespace-nowrap">
              Rows per page:
            </Text>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger
                className="h-8 w-[80px] shrink-0"
                data-testid="data-table-page-size"
                aria-label="Rows per page"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((size) => (
                  <SelectItem
                    key={size}
                    value={String(size)}
                    data-testid={`data-table-page-size-${size}`}
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => {
                table.previousPage();
              }}
              disabled={!canPreviousPage}
              data-testid="data-table-previous-page"
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft />
            </Button>
            <Text as="span" variant="body" className="whitespace-nowrap">
              Page {pageIndex + 1}
              {hasKnownPageCount ? ` of ${pageCount}` : ""}
            </Text>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => {
                table.nextPage();
              }}
              disabled={!canNextPage}
              data-testid="data-table-next-page"
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
